import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { AcademicianStartupButton } from "@/components/academician/academicianstartup-button";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FileAudioIcon,
  XIcon,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type InterviewType = "video-call" | "phone" | "in-person";

export interface Interview {
  id: string;
  contact: string;
  segment: string;
  phone: string;
  email: string;
  date: string;
  type: InterviewType;
  keyInsights: string;
  fullNotes: string;
  recordingUrl?: string;
  recordingFilename?: string;
}

export interface SegmentOption {
  id: string;
  name: string;
}

export interface InterviewsTabProps {
  interviews?: Interview[];
  segments?: SegmentOption[];
  onAdd?: (interview: Omit<Interview, "id">, file?: File | null, removeRecording?: boolean) => void;
  onEdit?: (id: string, interview: Omit<Interview, "id">, file?: File | null, removeRecording?: boolean) => void;
  onDelete?: (id: string) => void;
  className?: string;
}

const typeLabels: Record<InterviewType, string> = {
  "video-call": "Video Call",
  phone: "Phone",
  "in-person": "In-Person",
};

export function InterviewsTab({
  interviews = [],
  segments = [],
  onAdd,
  onEdit,
  onDelete,
  className,
}: InterviewsTabProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const [removeRecording, setRemoveRecording] = useState(false);
  const [existingRecording, setExistingRecording] = useState<{ url: string; filename: string } | null>(null);
  const [formData, setFormData] = useState({
    contact: "",
    segment: "",
    phone: "",
    email: "",
    date: "",
    type: "video-call" as InterviewType,
    keyInsights: "",
    fullNotes: "",
  });

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleOpenDialog = (interview?: Interview) => {
    setRecordingFile(null);
    setRemoveRecording(false);
    if (interview) {
      setEditingId(interview.id);
      setExistingRecording(
        interview.recordingUrl
          ? { url: interview.recordingUrl, filename: interview.recordingFilename || "Recording" }
          : null
      );
      setFormData({
        contact: interview.contact,
        segment: interview.segment,
        phone: interview.phone || "",
        email: interview.email || "",
        date: interview.date,
        type: interview.type,
        keyInsights: interview.keyInsights,
        fullNotes: interview.fullNotes,
      });
    } else {
      setEditingId(null);
      setExistingRecording(null);
      setFormData({
        contact: "",
        segment: "",
        phone: "",
        email: "",
        date: "",
        type: "video-call",
        keyInsights: "",
        fullNotes: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = () => {
    if (editingId) {
      onEdit?.(editingId, formData, recordingFile, removeRecording);
    } else {
      onAdd?.(formData, recordingFile);
    }
    setIsDialogOpen(false);
  };

  return (
    <div className={className}>
      {/* Add Button */}
      <div className="flex justify-end mb-4">
        <AcademicianStartupButton
          variant="primary"
          leftIcon={<PlusIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Interview
        </AcademicianStartupButton>
      </div>

      {/* Table */}
      <p className="text-sm font-semibold text-primary italic mb-2">For Customer Discovery and Validation Purposes</p>
      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[5%]"></TableHead>
              <TableHead className="w-[18%]">Contact</TableHead>
              <TableHead className="w-[15%]">Customer</TableHead>
              <TableHead className="w-[12%]">Date</TableHead>
              <TableHead className="w-[12%]">Type</TableHead>
              <TableHead className="w-[28%]">Key Insights</TableHead>
              <TableHead className="w-[10%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {interviews.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No interviews yet. Click "Add Interview" to get started.
                </TableCell>
              </TableRow>
            ) : (
              interviews.map((interview) => {
                const isExpanded = expandedRows.has(interview.id);
                return (
                  <>
                    <TableRow
                      key={interview.id}
                      className="cursor-pointer hover:bg-muted/50"
                    >
                      <TableCell onClick={() => toggleRow(interview.id)}>
                        {isExpanded ? (
                          <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell
                        className="font-medium"
                        onClick={() => toggleRow(interview.id)}
                      >
                        {interview.contact}
                      </TableCell>
                      <TableCell onClick={() => toggleRow(interview.id)}>
                        {interview.segment}
                      </TableCell>
                      <TableCell
                        className="text-sm text-muted-foreground"
                        onClick={() => toggleRow(interview.id)}
                      >
                        {interview.date}
                      </TableCell>
                      <TableCell onClick={() => toggleRow(interview.id)}>
                        <Badge variant="outline">
                          {typeLabels[interview.type]}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="text-sm"
                        onClick={() => toggleRow(interview.id)}
                      >
                        {interview.keyInsights}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <AcademicianStartupButton
                            variant="ghost"
                            size="sm"
                            leftIcon={<PencilIcon />}
                            onClick={() => handleOpenDialog(interview)}
                            className="px-2"
                          />

                          <AcademicianStartupButton
                            variant="ghost"
                            size="sm"
                            leftIcon={<TrashIcon />}
                            onClick={() => onDelete?.(interview.id)}
                            className="px-2 text-destructive hover:text-destructive"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-muted/30 p-6">
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <h4 className="font-semibold text-sm text-foreground">
                                Full Interview Notes
                              </h4>
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                                {interview.fullNotes}
                              </p>
                            </div>
                            {interview.recordingUrl && (
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-foreground">
                                  Recording
                                </h4>
                                <a
                                  href={interview.recordingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                                >
                                  <FileAudioIcon className="w-4 h-4" />
                                  {interview.recordingFilename || "Download Recording"}
                                </a>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Interview" : "Add Interview"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update the interview details below."
                : "Add a new interview record."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact">Contact Name</Label>
                <Input
                  id="contact"
                  value={formData.contact}
                  onChange={(e) =>
                    setFormData({ ...formData, contact: e.target.value })
                  }
                  placeholder="Contact name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="segment">Customer</Label>
                {segments.length > 0 ? (
                  <Select
                    value={formData.segment}
                    onValueChange={(value) =>
                      setFormData({ ...formData, segment: value })
                    }
                  >
                    <SelectTrigger id="segment">
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {segments.map((seg) => (
                        <SelectItem key={seg.id} value={seg.name}>
                          {seg.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="segment"
                    value={formData.segment}
                    onChange={(e) =>
                      setFormData({ ...formData, segment: e.target.value })
                    }
                    placeholder="Add customers in the Customers tab first"
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">H/P Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="e.g., +60 12-345 6789"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="e.g., contact@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Interview Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Interview Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, type: value as InterviewType })
                  }
                >
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video-call">Video Call</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="in-person">In-Person</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="keyInsights">Key Insights and New Learnings</Label>
              <Textarea
                id="keyInsights"
                value={formData.keyInsights}
                onChange={(e) =>
                  setFormData({ ...formData, keyInsights: e.target.value })
                }
                placeholder="Summarize the key insights, new discovery and learnings from this interview that will change your business model"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullNotes">Full Interview Notes</Label>
              <Textarea
                id="fullNotes"
                value={formData.fullNotes}
                onChange={(e) =>
                  setFormData({ ...formData, fullNotes: e.target.value })
                }
                placeholder="Detailed notes from the interview"
                rows={8}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recording">Recording (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Upload an audio or video recording (MP4, MOV, WebM, MP3, WAV, M4A, max 500MB)
              </p>
              {existingRecording && !removeRecording && !recordingFile && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                  <FileAudioIcon className="w-4 h-4 text-muted-foreground" />
                  <a
                    href={existingRecording.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex-1 truncate"
                  >
                    {existingRecording.filename}
                  </a>
                  <button
                    type="button"
                    onClick={() => setRemoveRecording(true)}
                    className="p-1 hover:bg-destructive/10 rounded"
                    title="Remove recording"
                  >
                    <XIcon className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              )}
              {(removeRecording || !existingRecording || recordingFile) && (
                <Input
                  id="recording"
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,audio/mpeg,audio/mp4,audio/wav,audio/x-m4a"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setRecordingFile(file);
                    if (file) setRemoveRecording(false);
                  }}
                />
              )}
              {recordingFile && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileAudioIcon className="w-4 h-4" />
                  <span className="truncate">{recordingFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setRecordingFile(null)}
                    className="p-1 hover:bg-destructive/10 rounded"
                  >
                    <XIcon className="w-3 h-3 text-destructive" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <AcademicianStartupButton
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </AcademicianStartupButton>
            <AcademicianStartupButton
              variant="primary"
              onClick={handleSave}
              disabled={
                !formData.contact || !formData.segment || !formData.date
              }
            >
              {editingId ? "Save Changes" : "Add Interview"}
            </AcademicianStartupButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
